import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Button, Tabs } from '@artosera/ui';
import { lireManifeste, lirePageManifeste, type EntreeManifeste } from '../../../lib/depots.js';
import { formaterOctets } from '../../../lib/format.js';
import tableStyles from '../../../styles/table.module.css';
import styles from './Reprise.module.css';

/** Un dépôt peut compter des milliers de fichiers : la liste s'arrête là. */
const LIGNES_MAX = 500;

function Liste({ fichiers, avecMotif }: { fichiers: EntreeManifeste[]; avecMotif: boolean }) {
  const { t } = useTranslation('console');
  if (fichiers.length === 0)
    return <p className={styles.discret}>{t('reprise.depots.manifeste.aucun')}</p>;
  return (
    <>
      <table className={`${tableStyles.table} ${styles.table}`}>
        <thead>
          <tr>
            <th scope="col">{t('reprise.depots.manifeste.chemin')}</th>
            <th scope="col" className={styles.colonneEtroite}>
              {t('reprise.depots.manifeste.taille')}
            </th>
            {avecMotif ? <th scope="col">{t('reprise.depots.manifeste.motif')}</th> : null}
          </tr>
        </thead>
        <tbody>
          {fichiers.slice(0, LIGNES_MAX).map((fichier) => (
            <tr key={fichier.id}>
              <td data-label={t('reprise.depots.manifeste.chemin')}>
                <span className={styles.chemin}>{fichier.chemin}</span>
              </td>
              <td data-label={t('reprise.depots.manifeste.taille')}>
                {formaterOctets(fichier.octets)}
              </td>
              {avecMotif ? (
                <td data-label={t('reprise.depots.manifeste.motif')}>
                  {t(`reprise.depots.motifs.${fichier.motif ?? fichier.etat}`, {
                    defaultValue: fichier.motif ?? fichier.etat,
                  })}
                  {fichier.detail ? <span className={styles.bloc}>{fichier.detail}</span> : null}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
      {fichiers.length > LIGNES_MAX ? (
        <p className={styles.discret}>
          {t('reprise.depots.manifeste.tronque', { count: LIGNES_MAX })}
        </p>
      ) : null}
    </>
  );
}

/**
 * Le manifeste d'un dépôt, tel que l'inventaire l'a rangé : les fichiers
 * refusés et pourquoi, puis les fichiers acceptés. Les refus d'abord : ce
 * sont eux qui demandent un geste.
 */
export function ManifesteDepot({
  idGalerie,
  depotId,
}: {
  idGalerie: string;
  depotId: string;
}): JSX.Element {
  const { t } = useTranslation('console');
  const [onglet, setOnglet] = useState<'refuses' | 'acceptes'>('refuses');
  const [page, setPage] = useState(1);
  const cle = ['console', 'galerie', idGalerie, 'reprise', 'depots', depotId, 'manifeste'];
  const index = useQuery({ queryKey: cle, queryFn: () => lireManifeste(idGalerie, depotId) });
  const fichiers = useQuery({
    queryKey: [...cle, page],
    queryFn: () => lirePageManifeste(idGalerie, depotId, page),
    enabled: index.isSuccess,
  });

  if (index.isLoading || fichiers.isLoading)
    return <p className={styles.discret}>{t('reprise.chargement')}</p>;
  if (!index.isSuccess || !fichiers.isSuccess)
    return (
      <p className={styles.erreur} role="alert">
        {t('reprise.depots.manifeste.erreur')}
      </p>
    );
  const pages = index.data.pages.length;
  const refuses = fichiers.data.filter(
    (fichier) => fichier.etat === 'refuse' || fichier.etat === 'en_attente_analyse',
  );
  const acceptes = fichiers.data.filter((fichier) => fichier.etat === 'accepte');
  return (
    <div className={styles.pile}>
      <h3 className={styles.sousTitre}>{t('reprise.depots.manifeste.titre')}</h3>
      {pages > 1 ? (
        <div className={styles.rangee}>
          <Button
            variant="ghost"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((courante) => courante - 1)}
          >
            {t('reprise.depots.manifeste.precedente')}
          </Button>
          <span className={styles.discret}>
            {t('reprise.depots.manifeste.page', { page, pages })}
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={page >= pages}
            onClick={() => setPage((courante) => courante + 1)}
          >
            {t('reprise.depots.manifeste.suivante')}
          </Button>
        </div>
      ) : null}
      <Tabs
        value={onglet}
        onValueChange={(valeur) => setOnglet(valeur === 'acceptes' ? 'acceptes' : 'refuses')}
        tabs={[
          {
            value: 'refuses',
            label: t('reprise.depots.manifeste.refuses', { count: refuses.length }),
            content: <Liste fichiers={refuses} avecMotif />,
          },
          {
            value: 'acceptes',
            label: t('reprise.depots.manifeste.acceptes', { count: acceptes.length }),
            content: <Liste fichiers={acceptes} avecMotif={false} />,
          },
        ]}
      />
    </div>
  );
}
